import { useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * ehr#60 - Provider Profile photos in the EHR's avatars. The photo is saved with the
 * profile (users.providerProfile.photo, a data URL); these read it back. Anything that
 * changes a photo calls notifyPhotosChanged() so every avatar on the page refetches.
 */
const EVENT = 'ehr:photos-changed';

export function notifyPhotosChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

function usePhotoFetch<T>(load: () => Promise<T>, empty: T, enabled: boolean): T {
  const [value, setValue] = useState<T>(empty);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const run = () =>
      load()
        .then((v) => live && setValue(v))
        .catch(() => {
          /* no photo is fine: the initials show */
        });
    run();
    window.addEventListener(EVENT, run);
    return () => {
      live = false;
      window.removeEventListener(EVENT, run);
    };
    // load is a stable module-level call per hook below
  }, [enabled]);
  return value;
}

/** The signed-in user's photo, or null. */
export function useMyPhoto(enabled = true): string | null {
  return usePhotoFetch(
    () => api.get<{ photo: string | null }>('/me/photo').then((r) => r.photo),
    null,
    enabled,
  );
}

/** Every team member's photo, by user id (Users & Profile; admins and managers). */
export function useTeamPhotos(): Record<string, string> {
  return usePhotoFetch(
    () => api.get<Record<string, string>>('/settings/users/photos'),
    {} as Record<string, string>,
    true,
  );
}
